/**
 * Dialogs: a bottom sheet on phones (a centred card on desktop) or a centred popup card.
 * Focus moves into the dialog, Tab stays inside, Escape and the backdrop close it, and focus
 * returns to where it was.
 */
import { useEffect, useRef, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import s from './Modal.module.css';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  onClose: () => void;
  /** id of the element that names the dialog. */
  labelledBy?: string;
  label?: string;
  variant?: 'sheet' | 'center';
  /** Extra class for the dialog panel. */
  className?: string;
  /** Extra class for the backdrop (e.g. a custom scene behind a popup). */
  backdropClassName?: string;
  /** Rendered behind the panel, inside the backdrop (rays, arena). */
  scene?: ReactNode;
  style?: CSSProperties;
  /** Element to focus first (defaults to the first focusable element). */
  initialFocus?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function Modal({
  onClose,
  labelledBy,
  label,
  variant = 'sheet',
  className,
  backdropClassName,
  scene,
  style,
  initialFocus,
  children,
}: ModalProps) {
  const panel = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const el = panel.current;
    const first = initialFocus?.current ?? el?.querySelector<HTMLElement>(FOCUSABLE) ?? el;
    first?.focus({ preventScroll: true });
    if (initialFocus?.current) initialFocus.current.scrollIntoView?.({ block: 'center' });
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !el) return;
      const items = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((x) => x.offsetParent !== null);
      if (!items.length) {
        e.preventDefault();
        return;
      }
      const firstItem = items[0]!;
      const lastItem = items[items.length - 1]!;
      if (e.shiftKey && (document.activeElement === firstItem || !el.contains(document.activeElement))) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus?.({ preventScroll: true });
    };
    // Runs once per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <div
      className={['cg-ui', s.backdrop, variant === 'sheet' ? s.backdropSheet : s.backdropCenter, backdropClassName ?? ''].join(' ')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {scene}
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : label}
        tabIndex={-1}
        className={[variant === 'sheet' ? s.sheet : s.center, className ?? ''].join(' ')}
        style={style}
      >
        {variant === 'sheet' && <div className={s.grip} aria-hidden="true" />}
        {children}
      </div>
    </div>,
    document.body,
  );
}
